import { FunctionRegistry, flattenArgs, toNum, toStr } from './index';
import { FormulaValue, FormulaError, FormulaErrorType } from '../types';
import { smartRound } from '../evaluator';

export function registerTextFunctions(registry: FunctionRegistry): void {
    // CHAR(number): returns the character represented by the given char code
    registry.register('CHAR', (...args: FormulaValue[]): FormulaValue => {
        const code = toNum(args[0]);
        if (code < 1 || code > 255) {
            throw new FormulaError(FormulaErrorType.VALUE, 'CHAR requires a number between 1 and 255');
        }
        return String.fromCharCode(Math.floor(code));
    });

    // CLEAN(text): removes non-printable characters (char codes 0–31)
    registry.register('CLEAN', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]);
        // eslint-disable-next-line no-control-regex
        return text.replace(/[\x00-\x1F]/g, '');
    });

    // CODE(text): returns the char code of the first character
    registry.register('CODE', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]);
        if (text.length === 0) {
            throw new FormulaError(FormulaErrorType.VALUE, 'CODE requires a non-empty string');
        }
        return text.charCodeAt(0);
    });

    // CONCATENATE(...args): joins all arguments as strings
    registry.register('CONCATENATE', (...args: FormulaValue[]): FormulaValue => {
        return args.map(a => toStr(a)).join('');
    });

    // EXACT(text1, text2): case-sensitive string comparison
    registry.register('EXACT', (...args: FormulaValue[]): FormulaValue => {
        const text1 = toStr(args[0]);
        const text2 = toStr(args[1]);
        return text1 === text2;
    });

    // FIND(findText, withinText, startNum?): case-sensitive search, 1-based index
    registry.register('FIND', (...args: FormulaValue[]): FormulaValue => {
        const findText = toStr(args[0]);
        const withinText = toStr(args[1]);
        const startNum = args[2] !== undefined && args[2] !== null ? toNum(args[2]) : 1;

        if (startNum < 1 || startNum > withinText.length + 1) {
            throw new FormulaError(FormulaErrorType.VALUE, 'FIND: startNum is out of range');
        }

        const idx = withinText.indexOf(findText, startNum - 1);
        if (idx === -1) {
            throw new FormulaError(FormulaErrorType.VALUE, 'FIND: substring not found');
        }
        return idx + 1;
    });

    // LEFT(text, numChars?): returns leftmost characters, default numChars=1
    registry.register('LEFT', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]);
        const numChars = args[1] !== undefined && args[1] !== null ? toNum(args[1]) : 1;
        if (numChars < 0) {
            throw new FormulaError(FormulaErrorType.VALUE, 'LEFT: numChars must be >= 0');
        }
        return text.substring(0, Math.floor(numChars));
    });

    // LEN(text): returns the length of the string
    registry.register('LEN', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]);
        return text.length;
    });

    // LOWER(text): converts to lowercase
    registry.register('LOWER', (...args: FormulaValue[]): FormulaValue => {
        return toStr(args[0]).toLowerCase();
    });

    // MID(text, startNum, numChars): 1-based substring
    registry.register('MID', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]);
        const startNum = toNum(args[1]);
        const numChars = toNum(args[2]);

        if (startNum < 1) {
            throw new FormulaError(FormulaErrorType.VALUE, 'MID: startNum must be >= 1');
        }
        if (numChars < 0) {
            throw new FormulaError(FormulaErrorType.VALUE, 'MID: numChars must be >= 0');
        }

        return text.substring(Math.floor(startNum) - 1, Math.floor(startNum) - 1 + Math.floor(numChars));
    });

    // N(value): converts to number — numbers pass through, TRUE=1, else 0
    registry.register('N', (...args: FormulaValue[]): FormulaValue => {
        const val = args[0];
        if (typeof val === 'number') return val;
        if (typeof val === 'boolean') return val ? 1 : 0;
        return 0;
    });

    // PROPER(text): capitalizes the first letter of each word
    registry.register('PROPER', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]);
        return text.replace(/\S+/g, word =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        );
    });

    // REPLACE(oldText, startNum, numChars, newText): 1-based replace
    registry.register('REPLACE', (...args: FormulaValue[]): FormulaValue => {
        const oldText = toStr(args[0]);
        const startNum = toNum(args[1]);
        const numChars = toNum(args[2]);
        const newText = toStr(args[3]);

        if (startNum < 1) {
            throw new FormulaError(FormulaErrorType.VALUE, 'REPLACE: startNum must be >= 1');
        }
        if (numChars < 0) {
            throw new FormulaError(FormulaErrorType.VALUE, 'REPLACE: numChars must be >= 0');
        }

        const start = Math.floor(startNum) - 1;
        const count = Math.floor(numChars);
        return oldText.substring(0, start) + newText + oldText.substring(start + count);
    });

    // REPT(text, count): repeats text count times
    registry.register('REPT', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]);
        const count = toNum(args[1]);
        if (count < 0) {
            throw new FormulaError(FormulaErrorType.VALUE, 'REPT: count must be >= 0');
        }
        return text.repeat(Math.floor(count));
    });

    // RIGHT(text, numChars?): returns rightmost characters, default numChars=1
    registry.register('RIGHT', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]);
        const numChars = args[1] !== undefined && args[1] !== null ? toNum(args[1]) : 1;
        if (numChars < 0) {
            throw new FormulaError(FormulaErrorType.VALUE, 'RIGHT: numChars must be >= 0');
        }
        const count = Math.floor(numChars);
        if (count >= text.length) return text;
        return text.substring(text.length - count);
    });

    // SEARCH(findText, withinText, startNum?): case-insensitive FIND
    registry.register('SEARCH', (...args: FormulaValue[]): FormulaValue => {
        const findText = toStr(args[0]).toLowerCase();
        const withinText = toStr(args[1]);
        const withinLower = withinText.toLowerCase();
        const startNum = args[2] !== undefined && args[2] !== null ? toNum(args[2]) : 1;

        if (startNum < 1 || startNum > withinText.length + 1) {
            throw new FormulaError(FormulaErrorType.VALUE, 'SEARCH: startNum is out of range');
        }

        const idx = withinLower.indexOf(findText, startNum - 1);
        if (idx === -1) {
            throw new FormulaError(FormulaErrorType.VALUE, 'SEARCH: substring not found');
        }
        return idx + 1;
    });

    // SUBSTITUTE(text, oldText, newText, instanceNum?): replace occurrences
    registry.register('SUBSTITUTE', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]);
        const oldText = toStr(args[1]);
        const newText = toStr(args[2]);
        const instanceNum = args[3] !== undefined && args[3] !== null ? toNum(args[3]) : null;

        if (oldText === '') return text;

        if (instanceNum === null) {
            // Replace all occurrences
            return text.split(oldText).join(newText);
        }

        // Replace only the nth occurrence (1-based)
        const target = Math.floor(instanceNum);
        if (target < 1) {
            throw new FormulaError(FormulaErrorType.VALUE, 'SUBSTITUTE: instanceNum must be >= 1');
        }

        let count = 0;
        let result = '';
        let searchStart = 0;

        while (searchStart <= text.length) {
            const idx = text.indexOf(oldText, searchStart);
            if (idx === -1) {
                result += text.substring(searchStart);
                break;
            }
            count++;
            if (count === target) {
                result += text.substring(searchStart, idx) + newText;
                result += text.substring(idx + oldText.length);
                break;
            }
            result += text.substring(searchStart, idx + oldText.length);
            searchStart = idx + oldText.length;
        }

        return result;
    });

    // T(value): returns the value if it is a string, empty string otherwise
    registry.register('T', (...args: FormulaValue[]): FormulaValue => {
        const val = args[0];
        if (typeof val === 'string') return val;
        return '';
    });

    // TEXT(value, format): basic number formatting
    registry.register('TEXT', (...args: FormulaValue[]): FormulaValue => {
        const value = toNum(args[0]);
        const format = toStr(args[1]);

        switch (format) {
            case '0':
                return Math.round(value).toString();
            case '0.00':
                return value.toFixed(2);
            case '#,##0': {
                return Math.round(value).toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                    useGrouping: true
                });
            }
            case '#,##0.00': {
                return value.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                    useGrouping: true
                });
            }
            case '0%':
                return `${Math.round(value * 100)}%`;
            case '0.00%':
                return `${(value * 100).toFixed(2)}%`;
            default:
                return value.toString();
        }
    });

    // TRIM(text): removes leading, trailing, and extra internal spaces
    registry.register('TRIM', (...args: FormulaValue[]): FormulaValue => {
        return toStr(args[0]).trim().replace(/\s+/g, ' ');
    });

    // UNICHAR(number): returns the character for the given Unicode code point
    registry.register('UNICHAR', (...args: FormulaValue[]): FormulaValue => {
        const code = Math.floor(toNum(args[0]));
        if (code < 1) {
            throw new FormulaError(FormulaErrorType.VALUE, 'UNICHAR requires a positive number');
        }
        try {
            return String.fromCodePoint(code);
        } catch {
            throw new FormulaError(FormulaErrorType.VALUE, 'UNICHAR: invalid code point');
        }
    });

    // UNICODE(text): returns the code point of the first character
    registry.register('UNICODE', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]);
        if (text.length === 0) {
            throw new FormulaError(FormulaErrorType.VALUE, 'UNICODE requires a non-empty string');
        }
        const cp = text.codePointAt(0);
        if (cp === undefined) {
            throw new FormulaError(FormulaErrorType.VALUE, 'UNICODE: could not determine code point');
        }
        return cp;
    });

    // UPPER(text): converts to uppercase
    registry.register('UPPER', (...args: FormulaValue[]): FormulaValue => {
        return toStr(args[0]).toUpperCase();
    });

    // VALUE(text): parses a number from text (handles comma as decimal separator)
    registry.register('VALUE', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]).trim();

        // Remove thousands separators and normalise decimal separator
        // Support both "1,234.56" (EN) and "1.234,56" (PT/DE) formats
        let normalised = text;

        // Detect format: if comma comes after period, it's the decimal separator (e.g. "1.234,56")
        const lastComma = normalised.lastIndexOf(',');
        const lastPeriod = normalised.lastIndexOf('.');

        if (lastComma > lastPeriod) {
            // Comma is decimal separator
            normalised = normalised.replace(/\./g, '').replace(',', '.');
        } else {
            // Period is decimal separator (or no decimal)
            normalised = normalised.replace(/,/g, '');
        }

        const num = parseFloat(normalised);
        if (isNaN(num)) {
            throw new FormulaError(FormulaErrorType.VALUE, `VALUE: cannot convert "${text}" to a number`);
        }
        return smartRound(num);
    });

    // Suppress unused import warnings for flattenArgs — it is exported for use by other function files
    void flattenArgs;
}
